import classNames from 'classnames';
import { memo, useMemo } from 'react';
import { useSubscriptionTypeTranslations } from '../../hooks/translations/useSubscriptionTypeTranslations';
import { T, useT } from '../../translation/src';
import { SubscriptionStatusFilter, SubscriptionType } from '../../types/graphqlTypes';

/** What stands out about a subscription: paused, cancelled (its next change ends it), both, or neither. */
export const subscriptionStatusesOf = ({ paused, scheduledType }: { paused: boolean; scheduledType?: SubscriptionType | null }): SubscriptionStatusFilter[] => [
  ...(paused ? [SubscriptionStatusFilter.Paused] : []),
  ...(scheduledType === SubscriptionType.Flex ? [SubscriptionStatusFilter.Cancelled] : []),
];

export const useSubscriptionStatusLabels = (): Record<SubscriptionStatusFilter, string> => {
  const paused = useT('paused', { swc: true });
  const cancelled = useT('cancelled', { swc: true });
  return useMemo(() => ({ [SubscriptionStatusFilter.Paused]: paused, [SubscriptionStatusFilter.Cancelled]: cancelled }), [paused, cancelled]);
};

const TAG_CLASSES: Record<SubscriptionStatusFilter, string> = {
  [SubscriptionStatusFilter.Paused]: 'bg-amber-500/20 text-amber-700 dark:text-amber-400',
  [SubscriptionStatusFilter.Cancelled]: 'bg-error-red-500/20 text-error-red-700 dark:text-error-red-500',
};

// The statuses as small tags, for an organisation's card header.
const SubscriptionStatusTags_: React.FC<{ statuses: SubscriptionStatusFilter[] }> = ({ statuses }) => {
  const labels = useSubscriptionStatusLabels();
  return (
    <>
      {statuses.map((status) => (
        <span key={status} className={classNames('mr-2 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold', TAG_CLASSES[status])}>
          {labels[status]}
        </span>
      ))}
    </>
  );
};
export const SubscriptionStatusTags = memo(SubscriptionStatusTags_);

// A cheaper plan waiting for the end of the term, as a tag beside the statuses. Nothing otherwise (an end is "cancelled").
const DowngradeTag_: React.FC<{ scheduledType?: SubscriptionType | null }> = ({ scheduledType }) => {
  const plans = useSubscriptionTypeTranslations();
  if (!scheduledType || scheduledType === SubscriptionType.Flex) return null;
  return (
    <span className={classNames('mr-2 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold', TAG_CLASSES[SubscriptionStatusFilter.Paused])}>
      <T _str="downgrading to {plan}" args={{ plan: plans[scheduledType] }} swc />
    </span>
  );
};
export const DowngradeTag = memo(DowngradeTag_);
